import { useState, useRef } from "react";
import { toast } from "react-toastify";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

export const Crop = ({
  cropShape = "round",
  onChangeCropper,
  maxSizeMib = 2,
  guid,
  triggerUpload,
  isButtonHidden = false,
  cropBoxHeight = 10,
  cropBoxWidth = 10,
  imagePreview,
  buttonBackGroundColor = "#ccc",
  aspectRatio,
  height = 200,
  width = 200,
}) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const cropperRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const [image, setImage] = useState("");
  const [isCropperReady, setIsCropperReady] = useState(false);

const onChange = (e) => {
  e.preventDefault();
  let files;
  if (e.dataTransfer) {
    files = e.dataTransfer.files;
  } else if (e.target) {
    files = e.target.files;
  }

  if (files && files.length) {
    const file = files[0];
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
    // validate type
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG, WEBP, or GIF files are allowed.", {
        autoClose: 3000,
      });
      return;
    }
    // validate size
    if (file.size > maxSizeMib * 1024 * 1024) {
      toast.error(`File must be less than ${maxSizeMib} MB`, {
        autoClose: 3000,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      fileRef.current = file;
      setImage(reader.result);
      setIsCropperReady(false);
      setTimeout(() => {
        setIsOpen(true);
      }, 1000);
    };
    reader.readAsDataURL(file);
  }
};


  const b64toBlob = async (dataURI) => {
    const base64file = await fetch(dataURI);
    return base64file.blob();
  };

  const getCropData = async () => {
    try {
    const cropper = cropperRef.current?.cropper;
    const dataURL = cropper.getCroppedCanvas().toDataURL();
      if (!dataURL) {
        toast.error("Failed to process image. Please try again.", {
          autoClose: 3000,
        });
        return;
      }

      const fileCropped = await b64toBlob(dataURL);

      onChangeCropper({
        fileCropped,
        originalFile: fileRef.current,
        fileCroppedBase64: dataURL,
      });

      setIsOpen(false);
    } catch (error) {
      console.error("Error in getCropData:", error);
      toast.error("Failed to crop image. Please try again.", {
        autoClose: 3000,
      });
    }
  };

  const triggerInvokedFromParent = () => {
    inputRef?.current?.click();
  };
  const getCalculatedDimensions = () => {
    const baseWidth = typeof width === 'number' ? width : 
                     typeof width === 'string' ? parseInt(width) || 200 : 200;
    const baseHeight = typeof height === 'number' ? height : 
                      typeof height === 'string' ? parseInt(height) || 200 : 200;
    
    if (aspectRatio && typeof aspectRatio === 'number') {
      if (aspectRatio > 1) {
        return {
          width: `${baseWidth}px`,
          height: `${Math.round(baseWidth / aspectRatio)}px`
        };
      } else if (aspectRatio < 1) {
        return {
          width: `${Math.round(baseHeight * aspectRatio)}px`,
          height: `${baseHeight}px`
        };
      } else {
        return {
          width: `${baseWidth}px`,
          height: `${baseHeight}px`
        };
      }
    }
        return {
      width: typeof width === 'number' ? `${width}px` : 
             typeof width === 'string' ? width : '200px',
      height: typeof height === 'number' ? `${height}px` : 
              typeof height === 'string' ? height : '200px'
    };
  };

  const calculatedDimensions = getCalculatedDimensions();

  return (
    <div>
      <div
        style={{
          display: "block",
          width: calculatedDimensions.width,
          height: calculatedDimensions.height,
          background: buttonBackGroundColor,
          position: "relative",
          cursor: "pointer",
          minWidth: calculatedDimensions.width,
          minHeight: calculatedDimensions.height,
          maxWidth: calculatedDimensions.width,
          maxHeight: calculatedDimensions.height,
          borderRadius: cropShape === 'round' ? '50%' : 
                       cropShape === 'rect' ? '8px' : 
                       '0px',
          overflow: 'hidden',
        }}
        onClick={triggerInvokedFromParent}
      ><div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 10 }}>Choose image</div>
        <input
          style={{ display: "none" }}
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onChange}
        />
        {imagePreview && (
          <img
            src={imagePreview}
            alt="preview"
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              left: 0,
              top: 0,
              objectFit: aspectRatio && typeof aspectRatio === 'number' ? 
                        (aspectRatio > 1 ? "cover" : aspectRatio < 1 ? "contain" : "cover") : 
                        "cover",
              borderRadius: cropShape === 'round' ? '50%' : 
                           cropShape === 'rect' ? '8px' : 
                           '0px',
              objectPosition: 'center',
            }}
            onError={(e) => {
              console.warn('Image preview failed to load:', imagePreview);
              e.target.style.display = 'none';
            }}
            onLoad={(e) => {
              e.target.style.display = 'block';
            }}
          />
        )}
      </div>

      <Modal
        size={typeof aspectRatio === 'number' && aspectRatio > 2 ? "xl" : 
              typeof aspectRatio === 'number' && aspectRatio < 0.5 ? "sm" : 
              "lg"}
        backdrop="static"
        keyboard={false}
        show={modalIsOpen}
        onHide={() => setIsOpen(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop</Modal.Title>
        </Modal.Header>
        <Modal.Body className={"crop-" + cropShape}>
          <Cropper
            aspectRatio={aspectRatio || 1}
            src={image}
            zoomable={false}
            minCropBoxHeight={cropBoxHeight}
            minCropBoxWidth={cropBoxWidth}
            background={false}
            responsive={true}
            cropBoxResizable={true}
            checkOrientation={false}
            guides={true}
            viewMode={1}
            ref={cropperRef}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            size="sm"
            className="me-2"
            onClick={() => setIsOpen(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={getCropData}
          >
           Crop
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Crop;
